import { useMemo, useRef, useState } from 'react';
import {
	Combobox as ComboboxRoot,
	ComboboxChip,
	ComboboxChips,
	ComboboxChipsInput,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxItem,
	ComboboxList,
	ComboboxValue,
} from '../components/combobox';

type Option = { value: string; label: string };

const CREATE_PREFIX = '__create__';

export function MultiCombobox(props: {
	id?: string;
	value: string[];
	onChange: (value: string[], selectedValue: string | undefined) => void;
	options: Option[];
	placeholder: string;
	searchPlaceholder: string;
	commandEmpty: string;
	creatable?: boolean;
	createLabel?: (value: string) => string;
	onCreateNew?: (value: string) => void;
	variant?: 'default' | 'outline';
	children?: React.ReactNode;
	className?: string;
	isError?: boolean;
	readOnly?: boolean;
}) {
	const [searchValue, setSearchValue] = useState('');
	const chipsRef = useRef<HTMLDivElement>(null);

	// Build options including any custom values from current selection
	const baseOptions = useMemo(() => {
		const ids = new Set(props.options.map((o) => o.value));
		return props.creatable
			? [...props.options, ...props.value.filter((v) => v && !ids.has(v)).map((v) => ({ value: v, label: v }))]
			: props.options;
	}, [props.options, props.creatable, props.value]);

	// Check if the search value matches an existing option
	const hasExactMatch = baseOptions.some(
		(o) => o.value.toLowerCase() === searchValue.toLowerCase() || o.label.toLowerCase() === searchValue.toLowerCase(),
	);
	const showCreateOption = props.creatable && searchValue.trim() && !hasExactMatch;

	// Add "Create" option when applicable
	const { createLabel } = props;
	const allItems = useMemo(() => {
		if (!showCreateOption) return baseOptions;
		const label = createLabel ? createLabel(searchValue) : `Add "${searchValue}"`;
		return [...baseOptions, { value: `${CREATE_PREFIX}${searchValue}`, label }];
	}, [baseOptions, showCreateOption, searchValue, createLabel]);

	const selectedItems = useMemo(
		() => baseOptions.filter((o) => props.value.includes(o.value)),
		[baseOptions, props.value],
	);

	const labelByValue = useMemo(() => new Map(baseOptions.map((o) => [o.value, o.label])), [baseOptions]);

	return (
		<ComboboxRoot
			modal
			multiple
			autoHighlight={props.creatable}
			items={allItems}
			value={selectedItems}
			onValueChange={(newItems: Option[]) => {
				// Check if user selected a "Create" option
				const createItem = newItems.find((item) => item.value.startsWith(CREATE_PREFIX));
				if (createItem && props.onCreateNew) {
					const newValue = createItem.value.slice(CREATE_PREFIX.length);
					props.onCreateNew(newValue);
					if (props.creatable) setSearchValue('');
					return;
				}

				const newValues = newItems.map((item) => {
					if (item.value.startsWith(CREATE_PREFIX)) {
						return item.value.slice(CREATE_PREFIX.length);
					}
					return item.value;
				});
				const addedValue = newValues.find((v) => !props.value.includes(v));
				props.onChange(newValues, addedValue);
				if (addedValue && props.creatable) setSearchValue('');
			}}
			onInputValueChange={(inputValue) => setSearchValue(inputValue)}
			isItemEqualToValue={(a: Option, b: Option) => a.value === b.value}
			filter={
				props.creatable
					? (item: Option, query: string) => {
							// Always show the "Create" option
							if (item.value.startsWith(CREATE_PREFIX)) return true;
							if (!query) return true;
							return item.label.toLowerCase().includes(query.toLowerCase());
						}
					: undefined
			}
		>
			<ComboboxChips ref={chipsRef} className={props.className}>
				<ComboboxValue>
					{props.value.filter(Boolean).map((v, i, arr) => (
						<span key={v} className="contents">
							<ComboboxChip>
								{labelByValue.get(v) ?? v}
								{/* The comma span is now always in the DOM for non-last chips. In
  edit mode it's max-w-0 opacity-0 overflow-hidden (invisible,
  zero-width). In read-only it transitions to max-w-[1ch]
  opacity-100. The -ml-2 cancels the chip's internal gap-2 so the
  comma sits flush against the text. Since both max-width and
  opacity animate over 200ms, the comma fades in smoothly without
  any layout jump. */}
								{i < arr.length - 1 && (
									<span
										className={`-ml-2 overflow-hidden transition-[max-width,opacity] duration-200 ${props.readOnly ? 'max-w-[1ch] opacity-100' : 'max-w-0 opacity-0'}`}
									>
										,
									</span>
								)}
							</ComboboxChip>
						</span>
					))}
				</ComboboxValue>
				<ComboboxChipsInput
					id={props.id}
					aria-invalid={props.isError || undefined}
					placeholder={props.searchPlaceholder}
				/>
			</ComboboxChips>
			<ComboboxContent anchor={chipsRef}>
				<ComboboxEmpty>{props.commandEmpty}</ComboboxEmpty>
				<ComboboxList>
					{(item: Option) => (
						<ComboboxItem key={item.value} value={item}>
							{item.label}
						</ComboboxItem>
					)}
				</ComboboxList>
			</ComboboxContent>
		</ComboboxRoot>
	);
}
